import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useWebsiteAdminAuth } from '../../context/WebsiteAdminAuthContext';
import { useAuth } from '../../../context/AuthContext';
import { mainAuth as auth } from '../../../auth/appAuth';
import {
  isPendingSecondFactorActive,
  isSecondFactorVerifiedSession,
} from '../../../features/two-factor-auth/utils/pendingSecondFactorSession';
import {
  hasWebsiteOwnerPortalMarker,
  shouldPreserveWebsitePortalDuringAppAuth,
} from '../../utils/websiteAuthSession';

const APP_ONLY_ROLES = new Set(['employee', 'manager']);

const WEBSITE_SIGNUP_PATHS = ['/main/account-setup', '/main/verify-email'];

const RedirectEmployeeToAppLogin = ({ resumeSecondFactor = false }) => {
  const { clearHydratedAppState, clearAppAuthSession } = useAuth();

  useEffect(() => {
    clearHydratedAppState();
    if (!resumeSecondFactor) {
      void clearAppAuthSession();
    }
  }, [clearAppAuthSession, clearHydratedAppState, resumeSecondFactor]);

  return (
    <Navigate
      to="/app/login"
      replace
      state={resumeSecondFactor ? { resumeAppSecondFactor: true } : undefined}
    />
  );
};

/**
 * When a user is logged into the app as an employee/manager (not owner), redirect them to the app
 * instead of allowing them to use the main marketing website.
 * Website admins (isSpecialAdmin) are not redirected so they can use the marketing site and admin features.
 */
const EmployeeWebsiteRedirect = ({ children }) => {
  const { pathname } = useLocation();
  const { adminUser, isSpecialAdmin, loading: websiteAdminLoading } = useWebsiteAdminAuth();
  const { user: mainAppUser, loading: authLoading } = useAuth();

  if (websiteAdminLoading || authLoading) {
    if (pathname === '/') {
      return children;
    }
    return null;
  }

  // Don't redirect website admins — they need to use the marketing site.
  if (adminUser && isSpecialAdmin) {
    return children;
  }

  const accountSetupInProgress =
    typeof sessionStorage !== 'undefined'
    && sessionStorage.getItem('accountSetupInProgress') === 'true';

  if (accountSetupInProgress || WEBSITE_SIGNUP_PATHS.some((path) => pathname.startsWith(path))) {
    return children;
  }

  const sessionUid = auth.currentUser?.uid;
  const uid = mainAppUser?.uid || sessionUid;

  // Fully signed-in app users (including employees with 2FA completed) go to the app.
  if (mainAppUser && APP_ONLY_ROLES.has(mainAppUser.role)) {
    if (uid && isSecondFactorVerifiedSession(uid)) {
      return <Navigate to="/app/overview" replace />;
    }

    const canResumeSecondFactor =
      Boolean(sessionUid) || isPendingSecondFactorActive();

    return <RedirectEmployeeToAppLogin resumeSecondFactor={canResumeSecondFactor} />;
  }

  // Session without a loaded profile — common when 2FA is still pending in the app tab.
  if (
    sessionUid &&
    !isSecondFactorVerifiedSession(sessionUid) &&
    !shouldPreserveWebsitePortalDuringAppAuth(sessionUid) &&
    !hasWebsiteOwnerPortalMarker(sessionUid)
  ) {
    return <RedirectEmployeeToAppLogin resumeSecondFactor />;
  }

  return children;
};

export default EmployeeWebsiteRedirect;
